import { useMemo, useState } from "react";
import moment from "moment-timezone";
import { type Table } from "@tanstack/react-table";

import {
  useGetCurrentAgtechReleaseBreakdown,
  AgtechReleaseBreakdownItem,
  AgtechReleaseBreakdownResponse,
} from "@/lib/api/cfr";
import { CommodityOptions } from "@/lib/api/commodity";
import { CommonDestinationTimezone } from "@/lib/api/utils";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { IndeterminateCheckbox } from "./IndeterminateCheckbox";
import { BulkAgtechReleaseSendDialog } from "./BulkAgtechReleaseSendDialog";
import { CfrAgtechReleaseReportDialog } from "./CfrAgtechReleaseReportDialog";

type BreakdownTab = "notSent" | "sent" | "signed" | "other";

function commodityLabel(commodity: string): string {
  return CommodityOptions.find((option) => option.value === commodity)?.label || commodity;
}

function formatWireDate(value: string | null | undefined): string {
  if (!value) return "";
  return moment.tz(value, CommonDestinationTimezone).format("YYYY/MM/DD");
}

export function AgtechReleaseReport() {
  const currentResult = useGetCurrentAgtechReleaseBreakdown();

  const [selectedTab, setSelectedTab] = useState<BreakdownTab>("notSent");
  const [isBulkSendDialogOpen, setIsBulkSendDialogOpen] = useState(false);
  const [selectedNotSentRows, setSelectedNotSentRows] = useState<AgtechReleaseBreakdownItem[]>([]);

  // Data is generated from the upload modal; this component only reads the
  // current persisted report, which the generate mutation invalidates.
  const breakdownData: AgtechReleaseBreakdownResponse | null =
    currentResult.data?.success ? currentResult.data.data : null;

  const isCurrentLoading = currentResult.isLoading;
  const isCurrentError = currentResult.error || (!!currentResult.data && !currentResult.data.success);

  // ---------- Status table columns ----------

  const { columns, rows } = useMemo((): {
    columns: DataTableColumn<AgtechReleaseBreakdownItem>[];
    rows: AgtechReleaseBreakdownItem[];
  } => {
    const baseColumns: DataTableColumn<AgtechReleaseBreakdownItem>[] = [
      {
        id: "commodity_type",
        accessorKey: "commodity_type",
        header: "Commodity",
        accessorFn: (row: AgtechReleaseBreakdownItem) => commodityLabel(row.commodity_type || ""),
      },
      {
        id: "crop_year",
        accessorKey: "crop_year",
        header: "Crop Year",
      },
      {
        id: "customer",
        accessorKey: "customer",
        header: "Customer",
        accessorFn: (row: AgtechReleaseBreakdownItem) => row.customer?.long_name || "N/A",
      },
      {
        id: "destination",
        accessorKey: "destination",
        header: "Destination",
        accessorFn: (row: AgtechReleaseBreakdownItem) => row.destination?.long_name || "N/A",
      },
      {
        id: "risk_projected_quantity",
        accessorKey: "risk_projected_quantity",
        header: "Risk Projected Qty",
        accessorFn: (row: AgtechReleaseBreakdownItem) => row.risk_projected_quantity ?? "",
      },
      {
        id: "agtech_live_quantity",
        accessorKey: "agtech_live_quantity",
        header: "Live Agtech Qty",
        accessorFn: (row: AgtechReleaseBreakdownItem) => row.agtech_live_quantity ?? "",
      },
      {
        id: "resolved_contract_quantity",
        accessorKey: "resolved_contract_quantity",
        header: "Send Qty",
        accessorFn: (row: AgtechReleaseBreakdownItem) => row.resolved_contract_quantity ?? "",
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Created",
        columnFilterType: "date",
        filterDateTimezone: CommonDestinationTimezone,
        filterDisplayFn: (v: unknown) => moment.tz(v as string, CommonDestinationTimezone).format("YYYY/MM/DD"),
        filterParseDateFn: (v: unknown) => moment.tz(v as string, CommonDestinationTimezone),
        accessorFn: (row: AgtechReleaseBreakdownItem) => row.created_at,
        cell: ({ row }: { row: { original: AgtechReleaseBreakdownItem } }) => (
          <span>{formatWireDate(row.original.created_at)}</span>
        ),
      },
    ];

    if (selectedTab === "notSent") {
      baseColumns.unshift({
        id: "selection-box",
        enableFiltering: false,
        enableSorting: false,
        disableRowClick: true,
        header: ({ table }: { table: Table<AgtechReleaseBreakdownItem> }) => (
          <IndeterminateCheckbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }: { row: import("@tanstack/react-table").Row<AgtechReleaseBreakdownItem> }) => (
          <IndeterminateCheckbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            indeterminate={row.getIsSomeSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
        size: 50,
      });
    }

    if (selectedTab === "sent" || selectedTab === "signed" || selectedTab === "other") {
      baseColumns.push({
        id: "last_status",
        accessorKey: "last_status",
        header: "Status",
        accessorFn: (row: AgtechReleaseBreakdownItem) => row.last_status || "",
      });
    }

    if (selectedTab === "signed") {
      baseColumns.push({
        id: "completed_at",
        accessorKey: "completed_at",
        header: "Completed",
        columnFilterType: "date",
        filterDateTimezone: CommonDestinationTimezone,
        filterDisplayFn: (v: unknown) => moment.tz(v as string, CommonDestinationTimezone).format("YYYY/MM/DD"),
        filterParseDateFn: (v: unknown) => moment.tz(v as string, CommonDestinationTimezone),
        accessorFn: (row: AgtechReleaseBreakdownItem) => row.completed_at ?? "",
        cell: ({ row }: { row: { original: AgtechReleaseBreakdownItem } }) => (
          <span>{formatWireDate(row.original.completed_at)}</span>
        ),
      });
    }

    if (selectedTab === "other") {
      const dateColumn = (
        id: "cancelled_at" | "revoked_at" | "declined_at" | "expired_at",
        header: string,
      ): DataTableColumn<AgtechReleaseBreakdownItem> => ({
        id,
        accessorKey: id,
        header,
        columnFilterType: "date",
        filterDateTimezone: CommonDestinationTimezone,
        filterDisplayFn: (v: unknown) => moment.tz(v as string, CommonDestinationTimezone).format("YYYY/MM/DD"),
        filterParseDateFn: (v: unknown) => moment.tz(v as string, CommonDestinationTimezone),
        accessorFn: (row: AgtechReleaseBreakdownItem) => row[id] ?? "",
        cell: ({ row }: { row: { original: AgtechReleaseBreakdownItem } }) => (
          <span>{formatWireDate(row.original[id])}</span>
        ),
      });
      baseColumns.push(
        dateColumn("cancelled_at", "Cancelled"),
        dateColumn("revoked_at", "Revoked"),
        dateColumn("declined_at", "Declined"),
        dateColumn("expired_at", "Expired"),
      );
    }

    let tabRows: AgtechReleaseBreakdownItem[] = [];
    if (breakdownData) {
      switch (selectedTab) {
        case "notSent":
          tabRows = breakdownData.notSent || [];
          break;
        case "sent":
          tabRows = breakdownData.sent || [];
          break;
        case "signed":
          tabRows = breakdownData.signed || [];
          break;
        case "other":
          tabRows = breakdownData.other || [];
          break;
      }
    }

    return { columns: baseColumns, rows: tabRows };
  }, [selectedTab, breakdownData]);

  const handleBulkDialogOpenChange = (open: boolean) => {
    setIsBulkSendDialogOpen(open);
    if (!open) {
      setSelectedNotSentRows([]);
    }
  };

  const cfrStatusReportButton = () => <CfrAgtechReleaseReportDialog />;

  const notSentBulkSendButton = ({ table }: { table: Table<AgtechReleaseBreakdownItem> }) => {
    const selectedRows = table.getFilteredSelectedRowModel().rows;
    return (
      <Button
        variant="outline"
        onClick={() => {
          setSelectedNotSentRows(selectedRows.map((row: { original: AgtechReleaseBreakdownItem }) => row.original));
          setIsBulkSendDialogOpen(true);
        }}
        disabled={selectedRows.length === 0}
      >
        Bulk Send ({selectedRows.length})
      </Button>
    );
  };

  const notSentCount = breakdownData?.notSent?.length || 0;
  const sentCount = breakdownData?.sent?.length || 0;
  const signedCount = breakdownData?.signed?.length || 0;
  const otherCount = breakdownData?.other?.length || 0;

  return (
    <div className="h-full flex flex-col gap-4">
      {isCurrentError && (
        <div className="text-sm text-destructive">
          Failed to load the current Agtech Release Report. Please refresh and try again.
        </div>
      )}

      {/* Status tables */}
      <Tabs value={selectedTab} onValueChange={(value: string) => setSelectedTab(value as BreakdownTab)} className="h-full flex flex-col">
        <TabsList className="flex flex-row gap-2 mb-2">
          <TabsTrigger value="notSent">To Send ({notSentCount})</TabsTrigger>
          {/* <TabsTrigger value="sent">Sent ({sentCount})</TabsTrigger> */}
          {/* <TabsTrigger value="signed">Signed ({signedCount})</TabsTrigger> */}
          {/* <TabsTrigger value="other">Other ({otherCount})</TabsTrigger> */}
        </TabsList>

        <TabsContent value="notSent" className="mt-0">
          <span className="text-sm text-gray-500">Lists the contracts to send as of the last Generated Contract release report</span>
          <DataTable
            stickyHeaders
            tableClassName="h-[calc(50vh-200px)] overflow-y-scroll"
            data={rows}
            columns={columns}
            enableSorting
            enableMultiSort
            enableFiltering
            enableGrouping
            enableColumnVisibility
            enableMultiSelect={selectedTab === "notSent"}
            customToolbarButtons={[cfrStatusReportButton, notSentBulkSendButton]}
            filterStyle="select"
            filterMode="internal"
            emptyMessage={isCurrentLoading ? "Loading ..." : "No items found"}
            defaultSorting={[{ id: "created_at", desc: true }]}
          />
        </TabsContent>

        <TabsContent value="sent" className="mt-0">
          <DataTable
            stickyHeaders
            tableClassName="h-[calc(50vh-200px)] overflow-y-scroll"
            data={rows}
            columns={columns}
            enableSorting
            enableMultiSort
            enableFiltering
            enableGrouping
            enableColumnVisibility
            customToolbarButtons={[cfrStatusReportButton]}
            filterStyle="select"
            filterMode="internal"
            emptyMessage={isCurrentLoading ? "Loading ..." : "No items found"}
            defaultSorting={[{ id: "created_at", desc: true }]}
          />
        </TabsContent>

        <TabsContent value="signed" className="mt-0">
          <DataTable
            stickyHeaders
            tableClassName="h-[calc(50vh-200px)] overflow-y-scroll"
            data={rows}
            columns={columns}
            enableSorting
            enableMultiSort
            enableFiltering
            enableGrouping
            enableColumnVisibility
            customToolbarButtons={[cfrStatusReportButton]}
            filterStyle="select"
            filterMode="internal"
            emptyMessage={isCurrentLoading ? "Loading ..." : "No items found"}
            defaultSorting={[{ id: "completed_at", desc: true }]}
          />
        </TabsContent>

        <TabsContent value="other" className="mt-0">
          <DataTable
            stickyHeaders
            tableClassName="h-[calc(50vh-200px)] overflow-y-scroll"
            data={rows}
            columns={columns}
            enableSorting
            enableMultiSort
            enableFiltering
            enableGrouping
            enableColumnVisibility
            customToolbarButtons={[cfrStatusReportButton]}
            filterStyle="select"
            filterMode="internal"
            emptyMessage={isCurrentLoading ? "Loading ..." : "No items found"}
            defaultSorting={[{ id: "created_at", desc: true }]}
          />
        </TabsContent>

        {isBulkSendDialogOpen && (
          <BulkAgtechReleaseSendDialog
            open={isBulkSendDialogOpen}
            onOpenChange={handleBulkDialogOpenChange}
            selectedItems={selectedNotSentRows}
          />
        )}
      </Tabs>
    </div>
  );
}
